import _ from 'underscore';
import { useCallback, useMemo, useContext } from 'react';
import {
  useAdPreviewModel as useAdPreviewModelCore,
  UAPLPlacementMetaList,
  UAPLPlacementMetaListV2,
  UAPPlacementType as EPlacementType,
  getExpandedImageInfo,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-core';
import { UAPCroppingType } from '@bingads-webui-campaign-react/unified-ad-preview-library-util';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';

export const PlacementType2AssociationTypeMap = {
  [EPlacementType.MMA_WideImage_4x1]: 'ImageMedia4X1',
  [EPlacementType.MMA_LandscapeImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.MMA_SquareImage_1x1]: 'SquareImageMedia',
  [EPlacementType.MMA_VerticalImage_1x2]: 'ImageMedia1X2',

  // audience ad (mma in msan)
  [EPlacementType.Audience_Network_WideImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Audience_Network_SquareImage_1x1]: 'SquareImageMedia',
  [EPlacementType.Microsoft_Edge_Single_Carousel_178x100]: 'ImageMedia178X100',
  [EPlacementType.MSN_InArticle_Ad_150x100]: 'ImageMedia15X10',
  [EPlacementType.MSN_Small_Headline_133x100]: 'ImageMedia133X100',

  // audience ad (mma in msan) V2
  [EPlacementType.MsanV2_Microsoft_Edge_Carouse_l201x100]: 'ImageMedia201X100',
  [EPlacementType.MsanV2_Microsoft_Edge_Mobile_1x1]: 'SquareImageMedia',
  [EPlacementType.MsanV2_MSAN_Banner_2x1]: 'ImageMedia200X100',
  [EPlacementType.MsanV2_MSAN_River_191x100]: 'LandscapeImageMedia',
  [EPlacementType.MsanV2_MSAN_Rectangle_120x100]: 'ImageMedia120X100',
  [EPlacementType.MsanV2_MSN_Carousel_169x100]: 'ImageMedia169X100',
  [EPlacementType.MsanV2_MSN_Partner_Stripe_153x100]: 'ImageMedia153X100',
  [EPlacementType.MsanV2_MSN_Restoday_124x100]: 'ImageMedia124X100',
  [EPlacementType.MsanV2_MSAN_Rectangle_Image_191x100]: 'LandscapeImageMedia',

  // boost
  [EPlacementType.Boost_Hero_Image_Carousel_200x100]: 'ImageMedia200X100',
  [EPlacementType.Boost_Hero_Image_Carousel_122x100]: 'ImageMedia122X100',
  [EPlacementType.Boost_Hero_Image_Carousel_159x100]: 'ImageMedia159X100',
  [EPlacementType.Boost_Hero_Image_Carousel_180x100]: 'ImageMedia180X100',
  [EPlacementType.Boost_Hero_Image_Carousel_233x100]: 'ImageMedia233X100',
  [EPlacementType.Boost_Hero_Image_Carousel_320x100]: 'ImageMedia320X100',
  [EPlacementType.Boost_Hero_Image_Carousel_57x100]: 'ImageMedia57X100',
  [EPlacementType.Boost_Windows_Spotlight_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Boost_Discover_Card_1x1]: 'SquareImageMedia',
  [EPlacementType.Boost_Prong1_Prong2_Feed_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Boost_Coachmark_175x100]: 'ImageMedia178X100',

  // image extension
  [EPlacementType.ImgExt_Audience_Network_WideImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.ImgExt_Audience_Network_SquareImage_1x1]: 'SquareImageMedia',
  [EPlacementType.ImgExt_Microsoft_Edge_Single_Carousel_178x100]: 'ImageMedia178X100',
  [EPlacementType.ImgExt_Bing_Desktop_SingleImage_Extension_178x100]: 'ImageMedia178X100',
  [EPlacementType.ImgExt_Microsoft_Edge_Medium_Headline_172x100]: 'ImageMedia172X100',
  [EPlacementType.ImgExt_MSN_Single_Carousel_169x100]: 'ImageMedia169X100',
  [EPlacementType.ImgExt_MSN_Medium_Headline_155x100]: 'ImageMedia155X100',
  [EPlacementType.ImgExt_MSN_InArticle_Ad_150x100]: 'ImageMedia15X10',
  [EPlacementType.ImgExt_MSN_Small_Headline_133x100]: 'ImageMedia133X100',
  [EPlacementType.ImgExt_Bing_Desktop_MultiImage_Extension_133x100]: 'ImageMedia133X100',
  [EPlacementType.ImgExt_Bing_Mobile_MultiImage_Extension_133x100]: 'ImageMedia133X100',
  [EPlacementType.ImgExt_MSN_Double_Carousel_93x100]: 'ImageMedia93X100',
  [EPlacementType.ImgExt_MMAV2_MSN_River_191x100]: 'LandscapeImageMedia',
  [EPlacementType.ImgExt_MMAV2_MSN_Single_Carousel_169x100]: 'ImageMedia169X100',
  [EPlacementType.ImgExt_MMAV2_MSN_Microsoft_Edge_Mobile_1x1]: 'SquareImageMedia',

  [EPlacementType.Responsive_MMA_WideImage_4x1]: 'ImageMedia4X1',
  [EPlacementType.Responsive_MMA_LandscapeImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_MMA_SquareImage_1x1]: 'SquareImageMedia',
  [EPlacementType.Responsive_MMA_VerticalImage_1x2]: 'ImageMedia1X2',

  // audience ad (mma in msan)
  [EPlacementType.Responsive_Audience_Network_WideImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_Audience_Network_SquareImage_1x1]: 'SquareImageMedia',
  [EPlacementType.Responsive_Microsoft_Edge_Single_Carousel_178x100]: 'ImageMedia178X100',
  [EPlacementType.Responsive_MSN_InArticle_Ad_150x100]: 'ImageMedia15X10',
  [EPlacementType.Responsive_MSN_Small_Headline_133x100]: 'ImageMedia133X100',

  [EPlacementType.Responsive_Boost_Windows_Spotlight_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_Boost_Discover_Card_1x1]: 'SquareImageMedia',
  [EPlacementType.Responsive_Boost_Prong1_Prong2_Feed_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_Boost_Coachmark_175x100]: 'ImageMedia178X100',
  [EPlacementType.Responsive_Boost_Mini_Spotlight]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_Boost_Content_Hub]: 'ImageMedia133X100',

  // audience ad (mma in msan) V2
  [EPlacementType.Responsive_MsanV2_Microsoft_Edge_Carouse_l201x100]: 'ImageMedia201X100',
  [EPlacementType.Responsive_MsanV2_Microsoft_Edge_Mobile_1x1]: 'SquareImageMedia',
  [EPlacementType.Responsive_MsanV2_MSAN_Banner_2x1]: 'ImageMedia200X100',
  [EPlacementType.Responsive_MsanV2_MSAN_River_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_MsanV2_MSAN_Rectangle_120x100]: 'ImageMedia120X100',
  [EPlacementType.Responsive_MsanV2_MSN_Carousel_169x100]: 'ImageMedia169X100',
  [EPlacementType.Responsive_MsanV2_MSN_Partner_Stripe_153x100]: 'ImageMedia153X100',
  [EPlacementType.Responsive_MsanV2_MSN_Restoday_124x100]: 'ImageMedia124X100',

  // LinkedIn
  [EPlacementType.LinkedIn_Single_Image_Desktop_Landscape]: 'LandscapeImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Mobile_Landscape]: 'LandscapeImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Medium_Rectangle_Landscape]: 'LandscapeImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Mobile_Interstitial_Landscape]: 'LandscapeImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Native_Landscape]: 'LandscapeImageMedia',

  [EPlacementType.LinkedIn_Single_Image_Desktop_Square]: 'SquareImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Mobile_Square]: 'SquareImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Medium_Rectangle_Square]: 'SquareImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Mobile_Interstitial_Square]: 'SquareImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Native_Square]: 'SquareImageMedia',

  [EPlacementType.LinkedIn_Single_Image_Mobile_Vertical]: 'PortraitImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Medium_Rectangle_Vertical]: 'PortraitImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Mobile_Interstitial_Vertical]: 'PortraitImageMedia',
  [EPlacementType.LinkedIn_Single_Image_Native_Vertical]: 'PortraitImageMedia',

  // Pmax
  [EPlacementType.PMax_MMA_WideImage_4x1]: 'ImageMedia4X1',
  [EPlacementType.PMax_MMA_LandscapeImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.PMax_MMA_VerticalImage_1x2]: 'ImageMedia1X2',
  [EPlacementType.PMax_Audience_Network_WideImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.PMax_Audience_Network_SquareImage_1x1]: 'SquareImageMedia',
  [EPlacementType.PMax_Microsoft_Edge_Single_Carousel_178x100]: 'ImageMedia178X100',
  [EPlacementType.PMax_MSN_InArticle_Ad_150x100]: 'ImageMedia15X10',
  [EPlacementType.PMax_MSN_Small_Headline_133x100]: 'ImageMedia133X100',
  // Pmax v2
  [EPlacementType.PMax_MMAV2_Microsoft_Edge_Single_Carousel_201x100]: 'ImageMedia201X100',
  [EPlacementType.PMax_MMAV2_MSN_Single_Carousel_169x100]: 'ImageMedia169X100',
  [EPlacementType.PMax_MMAV2_MSN_Restoday_124x100]: 'ImageMedia124X100',
  [EPlacementType.PMax_MMAV2_MSN_Partner_Stripe_153x100]: 'ImageMedia153X100',
  [EPlacementType.PMax_MMAV2_MSN_River_191x100]: 'LandscapeImageMedia',
  [EPlacementType.PMax_MMAV2_Microsoft_Edge_Mobile_1x1]: 'SquareImageMedia',

  // Pmax responsive
  [EPlacementType.Responsive_PMax_MMA_WideImage_4x1]: 'ImageMedia4X1',
  [EPlacementType.Responsive_PMax_MMA_LandscapeImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_PMax_MMA_VerticalImage_1x2]: 'ImageMedia1X2',
  [EPlacementType.Responsive_PMax_Audience_Network_WideImage_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_PMax_Audience_Network_SquareImage_1x1]: 'SquareImageMedia',
  [EPlacementType.Responsive_PMax_Microsoft_Edge_Single_Carousel_178x100]: 'ImageMedia178X100',
  [EPlacementType.Responsive_PMax_MSN_InArticle_Ad_150x100]: 'ImageMedia15X10',
  [EPlacementType.Responsive_PMax_MSN_Small_Headline_133x100]: 'ImageMedia133X100',

  // unified app preview
  [EPlacementType.Unified_App_Ads_Video_Horizontal]: 'Video16x9',
  [EPlacementType.Unified_App_Ads_Bing_Search]: 'ImageMedia133X100',
  [EPlacementType.Unified_App_Ads_Causal_Games_Dimensions]: 'SquareImageMedia',
  [EPlacementType.Unified_App_Ads_MSN_Display]: 'LandscapeImageMedia',

  // Pmax responsive v2
  [EPlacementType.Responsive_PMax_MMAV2_Microsoft_Edge_Single_Carousel_201x100]: 'ImageMedia201X100',
  [EPlacementType.Responsive_PMax_MMAV2_MSN_Single_Carousel_169x100]: 'ImageMedia169X100',
  [EPlacementType.Responsive_PMax_MMAV2_MSN_Restoday_124x100]: 'ImageMedia124X100',
  [EPlacementType.Responsive_PMax_MMAV2_MSN_Partner_Stripe_153x100]: 'ImageMedia153X100',
  [EPlacementType.Responsive_PMax_MMAV2_MSN_River_191x100]: 'LandscapeImageMedia',
  [EPlacementType.Responsive_PMax_MMAV2_Microsoft_Edge_Mobile_1x1]: 'SquareImageMedia',
};

const getExpandedImagePlacementInfo = (placement, hasSmartEffect) => {
  const {
    imageExpansionType, cropSettings, originalImageCropSettings,
  } = placement || {};
  const { CroppingType } = cropSettings || {};

  return getExpandedImageInfo({
    imageExpansionType,
    expandedImageUrl: placement.getExpandedImageUrl(),
    expandedImageWidth: placement.expandedImageWidth,
    expandedImageHeight: placement.expandedImageHeight,
    expandedImageCropSettings: placement.expandedImageCropSettings,
    originalImageCropSettings,
    extraCheck: () => hasSmartEffect || CroppingType === UAPCroppingType.ManualCrop,
  }) || {};
};

const convertEffectSettings = (effectSettings) => {
  if (!effectSettings) {
    return null;
  }
  const { effectKey, effectOptions, effectSrc } = effectSettings || {};
  const { backgroundBlur, backgroundColor, designerEffect } = effectOptions || {};
  const imageSmartEffect = effectSrc ? {
    effectType: effectKey,
    effectValue: backgroundBlur || backgroundColor || designerEffect,
    effectSrc,
  } : null;
  return imageSmartEffect;
};

// for image extension feature, it supports to replace images for specific placements, which results
// in different images in the 2nd dimension of imageExperienceImageList (placements)
// to handle it, we need to split the 2nd dimension of imageExperienceImageList into multiple parts
// by image src
const splitPlacementListBySrc = (imageExperienceImageList) => {
  // key is src, value is a list of placements with the same src
  let changed = false;
  const newImageList = [];
  _.forEach(imageExperienceImageList, (placementList) => {
    // for now, only split 2nd level, in case there may be images (1st dimension) with same URLs
    const placementListMap = _.groupBy(placementList, 'src');
    const srcList = _.keys(placementListMap);
    changed = changed || srcList.length > 1;
    srcList.forEach((src) => {
      newImageList.push(placementListMap[src]);
    });
  });
  return changed ? newImageList : imageExperienceImageList;
};

// imageExperienceImageList is a 2-dimensional array of type ImageExperienceImage,
// the first dimension is the 'Image', and the second dimension is the 'Placement' (objects with type of ImageExperienceImage)
// and the settings on image (crop/smart effetc) is presented by using 'baseImage', that is,
// for each 'Placement' list, they should have same 'baseImage' (not the same object, but with the same cropping and smart effects)
const transformImageExperienceImageList = (placementSetsConfig, imageExperienceImageList, isImageFittingEnabled, isAdPreviewRefreshEnabled) => {
  // filter out logos
  let imageList = _.compact(_.map(imageExperienceImageList, (imgExpPlacementList) => {
    const placements = _.filter(imgExpPlacementList, ({ isLogo }) => !isLogo);
    return _.isEmpty(placements) ? null : placements;
  })) || [];

  imageList = splitPlacementListBySrc(imageList);

  const PlacementMetaList = isAdPreviewRefreshEnabled ? UAPLPlacementMetaListV2 : UAPLPlacementMetaList;

  const previewImageList = _.map(imageList, (imgExpPlacementList /* [ImageExperienceImage] */) => {
    // image level
    let imageMeta = null;
    let imageCropping = null;
    let imageSmartEffect = null;
    let hasImageCroppingChanged = false;

    const placementSets = _.map(placementSetsConfig, ({ placementSetName, placementTypes }) => {
      const placementList = _.map(placementTypes, (placementType) => {
        const placementMeta = PlacementMetaList[placementType] || {};

        // get association type from placement type
        const curAssociationType = PlacementType2AssociationTypeMap[placementType] || '';

        // get the ImageExperienceImage object representing the placement
        const placement = _.find(imgExpPlacementList, ({ associationType }) => associationType && curAssociationType && (associationType.toLowerCase() === curAssociationType.toLowerCase()));

        if (!placement) {
          return {
            placementMeta,
            placementCropping: null,
            placementSmartEffect: null,
            hasCroppingChanged: false,
            isPlacementIncluded: false,
            // mark this placment not initialized
            hasImageExperienceData: false,
          };
        }

        // the BaseImage represents image info (bulk editing on all placements)
        const baseImage = placement.BaseImage || {};

        if (!imageMeta) {
          imageMeta = {
            id: baseImage.IdString || baseImage.Id || placement.Id,
            url: placement.Src || placement.OriginalUrl || baseImage.Src || baseImage.OriginalUrl,
            width: baseImage.Width || baseImage.OriginalWidth || placement.Width || placement.OriginalWidth,
            height: baseImage.Height || baseImage.OriginalHeight || placement.Height || placement.OriginalHeight,
            originalUrl: baseImage.OriginalUrl || baseImage.Src || placement.OriginalUrl || placement.Src,
            originalWidth: baseImage.OriginalWidth || baseImage.Width || placement.OriginalWidth || placement.Width,
            originalHeight: baseImage.OriginalHeight || baseImage.Height || placement.OriginalHeight || placement.Height,
          };
        }

        // image cropping
        if (!imageCropping) {
          imageCropping = baseImage.CropSettings;
        }

        // image smart effect
        if (!imageSmartEffect) {
          imageSmartEffect = convertEffectSettings(baseImage.EffectSettings);
        }

        // image flag
        if (baseImage.isMaunalCropped) {
          hasImageCroppingChanged = hasImageCroppingChanged || baseImage.isMaunalCropped();
        }

        // only set if there is placement level smart effect
        let placementSmartEffect = null;
        if (placement.EffectSettings && (!baseImage.EffectSettings || placement.EffectSettings.effectSrc !== baseImage.EffectSettings.effectSrc)) {
          placementSmartEffect = convertEffectSettings(placement.EffectSettings);
        }

        const {
          expandedImageCropSettings,
          expandedImageUrl,
          expandedImageWidth,
          expandedImageHeight,
        } = getExpandedImagePlacementInfo(placement, imageSmartEffect || placementSmartEffect);

        // For some scenarios, such as image annotation, we don't want to use imageFitting for the search preview, because DE doesn't support it for now.
        const applyImageFitting = !placementMeta.ignoreImageFitting && isImageFittingEnabled;

        // placement info
        return {
          placementMeta,
          placementCropping: placement.CropSettings || null,
          // !baseImage.EffectSettings means smart effect is set on placement level
          placementSmartEffect,
          hasCroppingChanged: (placement.isMaunalCropped && placement.isMaunalCropped()) || false,
          isPlacementIncluded: placement.isIncluded || false,
          // mark this placment initialized
          hasImageExperienceData: true,
          expandedImageUrl: applyImageFitting ? expandedImageUrl : undefined,
          expandedImageWidth: applyImageFitting ? expandedImageWidth : undefined,
          expandedImageHeight: applyImageFitting ? expandedImageHeight : undefined,
          expandedImageCropSettings: applyImageFitting ? expandedImageCropSettings : undefined,
        };
      });

      // placement set
      const placementSet = {
        name: placementSetName,
        placementList,
      };
      return placementSet;
    });

    const previewImage = {
      imageMeta,
      imageCropping,
      imageSmartEffect,
      hasCroppingChanged: hasImageCroppingChanged,
    };

    // get previewImage and placementSets
    return { previewImage, placementSets };
  });

  return previewImageList;
};

// this hook is a wrapper on useAdPreviewModelCore, to initialize hook state by using imageList
// returned from ImageExperience component
// the imageList is a 2-dimensional array of type ImageExperienceImage, the first dimension is the 'Image',
// and the second dimension is the 'Placement'
//
// @placementConfig: Array<UAPPlacementSetParam>
export const useImageExperienceAdPreviewModel = (placementConfig, placeholderImage, isImageFittingEnabled) => {
  const {
    resetReducerStates,
    setPlacementConfig,
    multiImagePlacementSets,
    ...rest
  } = useAdPreviewModelCore();

  const permissions = useContext(PermissionsContext);
  const isAdPreviewRefreshEnabled = _.get(permissions, ['dynamic', 'AdPreviewRefresh']);

  const isPlaceHolderImage = useCallback((imageMeta) => {
    const { url } = imageMeta || {};
    const placeHolderUrl = placeholderImage && (_.isString(placeholderImage) ? placeholderImage : placeholderImage.url);
    return url && placeHolderUrl && url === placeHolderUrl;
  }, [placeholderImage]);

  // only return the placementSets with image experience data
  const imageExperiencePlacementSets = useMemo(() => {
    let changed = false;
    const ret = _.map(multiImagePlacementSets, ({ placementSetName, placementList }) => {
      const newPlacementList = _.map(placementList, (placement) => {
        const { images } = placement || [];
        const newImages = _.filter(images, ({ hasImageExperienceData, imageMeta }) => hasImageExperienceData || isPlaceHolderImage(imageMeta)) || [];
        changed = changed || newImages.length !== images.length;
        return { ...placement, images: newImages };
      });
      return { placementSetName, placementList: newPlacementList };
    });
    return changed ? ret : multiImagePlacementSets;
  }, [isPlaceHolderImage, multiImagePlacementSets]);

  const onUpdateImageExperienceImageList = useCallback((imageExperienceImageList, activeImageIndices, hasDataChanged) => {
    const previewImageListState = transformImageExperienceImageList(placementConfig, imageExperienceImageList, isImageFittingEnabled, isAdPreviewRefreshEnabled);
    if (!_.isEmpty(previewImageListState)) {
      const activeImageIndexList = previewImageListState && previewImageListState.map((img, index) => index);
      resetReducerStates({ previewImageListState, activeImageIndices: activeImageIndices || activeImageIndexList || [0], hasDataChanged });
    } else if (placeholderImage) {
      // use placeholder image if no image is set
      setPlacementConfig([{ image: placeholderImage, placementSets: placementConfig }]);
    }
  }, [isImageFittingEnabled, placeholderImage, placementConfig, resetReducerStates, setPlacementConfig, isAdPreviewRefreshEnabled]);

  return {
    onUpdateImageExperienceImageList,
    multiImagePlacementSets,
    imageExperiencePlacementSets,
    ...rest,
  };
};
